import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { LazyMotion, domAnimation } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import "@/i18n";
import JourneyProvider from "@/components/JourneyProvider";
import HeaderHeightSensor from "@/components/HeaderHeightSensor";
import WhatsAppButton from "@/components/WhatsAppButton";
import RouteSeo from "@/components/RouteSeo";
import Navbar from "@/components/Navbar";
import TrustBar from "@/components/TrustBar";
import StickyCTA from "@/components/StickyCTA";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import CookieBanner from "@/components/CookieBanner";
import { bootConsent } from "@/lib/consent";
import { initTracking } from "@/lib/tracking/dispatcher";
import { CustomerAuthProvider } from "@/context/CustomerAuthContext";
import "@/App.css";
const PrivatePage = lazy(() => import("@/pages/PrivatePage"));
const BusinessPage = lazy(() => import("@/pages/BusinessPage"));
const ProductsPage = lazy(() => import("@/pages/ProductsPage"));
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const BookingSuccessPage = lazy(() => import("@/pages/BookingSuccessPage"));
const ReferencesPage = lazy(() => import("@/pages/ReferencesPage")); // eslint-disable-line no-unused-vars
const ProcessPage = lazy(() => import("@/pages/ProcessPage"));
const FaqPage = lazy(() => import("@/pages/FaqPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const LayoutsPage = lazy(() => import("@/pages/LayoutsPage"));
const LayoutDesignerPage = lazy(() => import("@/pages/LayoutDesignerPage"));
const AdminLoginPage = lazy(() => import("@/pages/AdminLoginPage"));
const AdminDashboardPage = lazy(() => import("@/pages/AdminDashboardPage"));
const AdminBookingPage = lazy(() => import("@/pages/AdminBookingPage"));
const CustomerLoginPage = lazy(() => import("@/pages/CustomerLoginPage"));
const CustomerRegisterPage = lazy(() => import("@/pages/CustomerRegisterPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const CustomerDashboardPage = lazy(() => import("@/pages/CustomerDashboardPage"));
const OfferAcceptPage = lazy(() => import("@/pages/OfferAcceptPage"));
const QuickSignupPage = lazy(() => import("@/pages/QuickSignupPage"));
const ImpressumPage = lazy(() => import("@/pages/ImpressumPage"));
const DatenschutzPage = lazy(() => import("@/pages/DatenschutzPage"));
const AgbPage = lazy(() => import("@/pages/AgbPage"));
const PosBookingPage = lazy(() => import("@/pages/PosBookingPage"));
const UnsubscribePage = lazy(() => import("@/pages/UnsubscribePage"));
const AdminActionPage = lazy(() => import("@/pages/AdminActionPage"));
const ReminderResolvePage = lazy(() => import("@/pages/ReminderResolvePage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const HilfePage = lazy(() => import("@/pages/HilfePage"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__SNAP_ACTIVE) return;  // Vorab-Fassung sichtbar: Leser nicht nach oben reißen
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

// Admin-Bereich heller + größere Schrift (index.css html.admin-hell), nur auf /admin-Seiten
function AdminTheme() {
  const { pathname } = useLocation();
  useEffect(() => {
    const on = pathname.startsWith("/admin") && !pathname.startsWith("/admin/login") && !pathname.startsWith("/admin-actions");
    document.documentElement.classList.toggle("admin-hell", on);
  }, [pathname]);
  return null;
}

function Shell() {
  return (
    <>
      <Navbar />
      <ScrollToTop />
      <AdminTheme />
      <JourneyProvider />
      <HeaderHeightSensor />
      <TrustBar />
      <main>
        <RouteSeo />
        <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/privatkunden" element={<PrivatePage />} />
          <Route path="/businesskunden" element={<BusinessPage />} />
          <Route path="/produkte" element={<ProductsPage />} />
          <Route path="/buchen" element={<BookingPage />} />
          <Route path="/buchung/erfolg" element={<BookingSuccessPage />} />
          {/* Session 12.6: Referenzen-Route + Nav-Link entfernt (bleibt als Datei, aber ohne Zugriff). */}
          <Route path="/ablauf" element={<ProcessPage />} />
          <Route path="/layouts" element={<LayoutsPage />} />
          <Route path="/layouts/designer" element={<LayoutDesignerPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/kontakt" element={<ContactPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route path="/admin/buchung/:bookingId" element={<AdminBookingPage />} />
          <Route path="/admin-actions/:token" element={<AdminActionPage />} />
          <Route path="/aktion/bereits-erledigt/:token" element={<ReminderResolvePage />} />
          <Route path="/kundenkonto/anmelden" element={<CustomerLoginPage />} />
          <Route path="/kundenkonto/registrieren" element={<CustomerRegisterPage />} />
          <Route path="/kundenkonto/passwort-vergessen" element={<ForgotPasswordPage />} />
          <Route path="/kundenkonto/passwort-neu" element={<ResetPasswordPage />} />
          <Route path="/kundenkonto/quick-signup" element={<QuickSignupPage />} />
          {/* Session 32c · Deep-Links aus Erinnerungs-/Reminder-E-Mails.
              Beide Routen zeigen dieselbe Dashboard-Seite, die den bookingId-
              Parameter liest, zur Buchung scrollt und die Detail-Blöcke öffnet.
              Bei fehlender Buchung erscheint eine freundliche Fallback-Card
              (kein 404). Alte E-Mails mit `/kundenkonto/bookings/…` werden so
              rückwirkend gültig: kein weiterer Kunden-404. */}
          <Route path="/kundenkonto/bookings/:bookingId" element={<CustomerDashboardPage />} />
          <Route path="/kundenkonto/buchung/:bookingId" element={<CustomerDashboardPage />} />
          <Route path="/kundenkonto" element={<CustomerDashboardPage />} />
          <Route path="/angebot/annehmen/:token" element={<OfferAcceptPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
          <Route path="/agb" element={<AgbPage />} />
          <Route path="/interne-buchung" element={<PosBookingPage />} />
          <Route path="/newsletter/abmelden" element={<UnsubscribePage />} />
          <Route path="/hilfe" element={<HilfePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </main>
      <StickyCTA />
      <WhatsAppButton />
      <Footer />
    </>
  );
}

export default function App() {
  useEffect(() => { bootConsent(); initTracking(); }, []);
  return (
    <div className="App">
      <LazyMotion features={domAnimation}>
      <BrowserRouter>
        <AuthProvider>
          <CustomerAuthProvider>
            <Shell />
            <CookieBanner />
            <Toaster theme="dark" richColors position="top-right" />
          </CustomerAuthProvider>
        </AuthProvider>
      </BrowserRouter>
      </LazyMotion>
    </div>
  );
}
